import { Component, computed, inject, model, signal } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { FULL_VIN_LENGTH, MAX_VINS, PARTIAL_VIN_LENGTH, VinEntry, VinKind, classifyVin, splitVins } from '../vin-entry';
import { VinLimitDialogComponent, VinLimitDialogData } from './vin-limit-dialog.component';

const KIND_LABEL: Record<VinKind | 'invalid', string> = {
  full: 'Full ' + FULL_VIN_LENGTH + ' characters' ,
  partial: 'Last ' + PARTIAL_VIN_LENGTH + ' characters',
  invalid: 'Invalid'
};

/**
 * Chip input for the VINs to look up. Values are added on comma or Enter, or
 * all at once from a paste; each is tagged full, last-4, or invalid. Invalid
 * values stay as chips rather than being dropped, so a typo in a pasted list
 * is visible and fixable — the page refuses to search until they're removed.
 */
@Component({
  selector: 'app-vin-input',
  imports: [MatFormFieldModule, MatChipsModule, MatIconModule],
  templateUrl: './vin-input.component.html',
  styleUrl: './vin-input.component.scss'
})
export class VinInputComponent {
  private readonly dialog = inject(MatDialog);

  readonly entries = model<VinEntry[]>([]);

  readonly max = MAX_VINS;
  readonly partialVINLength = PARTIAL_VIN_LENGTH;
  readonly fullVINLength = FULL_VIN_LENGTH;
  readonly separatorKeys = [ENTER, COMMA] as const;

  /** How many values the last add couldn't take because the list was full. */
  readonly dropped = signal(0);

  readonly counts = computed(() => {
    const entries = this.entries();
    return {
      total: entries.length,
      full: entries.filter(entry => entry.kind === 'full').length,
      partial: entries.filter(entry => entry.kind === 'partial').length,
      invalid: entries.filter(entry => entry.kind === null).length
    };
  });

  kindLabel(entry: VinEntry): string {
    return KIND_LABEL[entry.kind ?? 'invalid'];
  }

  add(event: MatChipInputEvent): void {
    this.addAll(splitVins(event.value));
    event.chipInput.clear();
  }

  /** A pasted list becomes chips at once; a single pasted value lands in the input like typing. */
  onPaste(event: ClipboardEvent): void {
    const values = splitVins(event.clipboardData?.getData('text') ?? '');
    if (values.length > 1) {
      event.preventDefault();
      this.addAll(values);
    }
  }

  remove(entry: VinEntry): void {
    this.entries.update(entries => entries.filter(existing => existing !== entry));
    this.dropped.set(0);
  }

  /** Appends new values, skipping duplicates; anything past the limit is listed in a dialog. */
  private addAll(values: string[]): void {
    const entries = this.entries();
    const seen = new Set(entries.map(entry => entry.value));
    const fresh: VinEntry[] = [];
    for (const entry of values.map(classifyVin)) {
      if (!seen.has(entry.value)) {
        seen.add(entry.value);
        fresh.push(entry);
      }
    }
    const room = Math.max(0, MAX_VINS - entries.length);
    const dropped = fresh.slice(room).map(entry => entry.value);
    this.dropped.set(dropped.length);
    this.entries.set([...entries, ...fresh.slice(0, room)]);

    if (dropped.length) {
      this.dialog.open<VinLimitDialogComponent, VinLimitDialogData>(VinLimitDialogComponent, {
        data: { max: MAX_VINS, dropped },
        width: '480px',
        maxWidth: '95vw'
      });
    }
  }
}
